import { useMemo } from "react";
import type { ExecTranscriptLine, ExecTranscriptResponse } from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { useExecTranscript } from "@/api/execsession";
import { useT } from "@/i18n/I18nProvider";
import { Notice } from "@/features/drill/sessionPanel";
import { dayClock } from "@/lib/format";

/**
 * 세션 기록 뷰어 — ADR 0031
 * --------------------------------------------------------------------------
 * 보여주는 것은 서버가 **정제·마스킹한 뒤 저장한 stdout·stderr 줄**뿐입니다. stdin은
 * 응답에 없고, 원문 조회·복사 경로도 없습니다 — 있는 것이 마스킹된 본문 전부입니다.
 * 가려진 구간은 로그 목록과 같은 방식(`.masked`)으로 표시합니다.
 *
 * 꺼진 배포·기록 없음·권한 없음·만료는 전부 서버 문구가 실린 404라, 화면은 그 문장을
 * 그대로 보여주고 사유를 지어내지 않습니다.
 *
 * 상자는 Debug Terminal과 같은 `.terminal` 껍데기입니다 — 높이가 고정이고 스크롤바가
 * 늘 자리를 차지합니다. 다만 **에뮬레이터가 아닙니다**(ADR 0032는 라이브 세션에만
 * 적용됩니다) — 기록은 이미 서버가 정제해 둔 텍스트라 그대로 그리면 됩니다.
 */

function Line({ line, maskedTitle }: { line: ExecTranscriptLine; maskedTitle: string }) {
  if (line.masked.length === 0) return <>{line.text}</>;
  const parts: Array<{ text: string; masked: boolean }> = [];
  let cursor = 0;
  for (const m of [...line.masked].sort((a, b) => a.start - b.start)) {
    if (m.start > cursor) parts.push({ text: line.text.slice(cursor, m.start), masked: false });
    parts.push({ text: line.text.slice(m.start, m.start + m.length), masked: true });
    cursor = m.start + m.length;
  }
  if (cursor < line.text.length) parts.push({ text: line.text.slice(cursor), masked: false });
  return (
    <>
      {parts.map((p, i) =>
        p.masked ? (
          <span key={i} className="masked" title={`${maskedTitle}`}>
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

export function ExecTranscriptView({ sessionId, onClose }: { sessionId: string; onClose?: () => void }) {
  const { t } = useT("terminal");
  const query = useExecTranscript(sessionId);
  const pages = query.data?.pages ?? [];
  const head: ExecTranscriptResponse | undefined = pages[0];
  const lines = useMemo(() => pages.flatMap((p) => p.lines), [pages]);
  const denied = query.error instanceof HttpError ? query.error.body.message : "";

  return (
    <section className="exec-record" data-testid="exec-transcript-view" aria-label={t("terminal.record.label")}>
      <div className="exec-toolbar">
        <strong>{t("terminal.record.title")}</strong>
        {head ? (
          <span className="muted">
            {head.namespace}/{head.podName} · {head.container} · {head.subject}
          </span>
        ) : null}
        {onClose ? (
          <button type="button" className="chip" onClick={onClose} data-testid="exec-transcript-close">
            {t("terminal.record.hide")}
          </button>
        ) : null}
      </div>
      {query.isLoading ? <p className="muted">{t("terminal.record.loading")}</p> : null}
      {denied ? (
        <Notice tone="warning" role="alert" testId="exec-transcript-denied">
          {denied}
        </Notice>
      ) : null}
      {head ? (
        <>
          <p className="muted">
            {t("terminal.record.policy", { days: head.retentionDays, expires: dayClock(Date.parse(head.expiresAt)) })}
            {" · "}
            {t("terminal.record.stdinNote")}
          </p>
          {head.live ? <Notice role="status">{t("terminal.record.live")}</Notice> : null}
          {head.truncated ? <Notice tone="warning" role="note">{t("terminal.record.truncated")}</Notice> : null}
          {head.failed ? <Notice tone="warning" role="note">{t("terminal.record.failed")}</Notice> : null}
          <div className="exec-screen" role="group" aria-label={t("terminal.record.label")} tabIndex={0}>
              {lines.length === 0 ? (
                <p className="muted">{t("terminal.record.empty")}</p>
              ) : (
                <pre>
                  {lines.map((line, i) => (
                    <span key={i} className="exec-record__line">
                      <Line line={line} maskedTitle={t("terminal.record.masked")} />
                      {"\n"}
                    </span>
                  ))}
                </pre>
              )}
          </div>
          {query.hasNextPage ? (
            <div className="exec-toolbar">
              <button type="button" className="btn btn--sm" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                {t("terminal.record.more")}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
